import { useEffect, useState, useCallback, Fragment } from "react";
import { api } from "@/lib/api";
import { ROLE_LABELS, formatDate } from "@/lib/labels";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollText, Search, ChevronLeft, ChevronRight, ChevronDown, ArrowRight } from "lucide-react";

const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "Sim";
  if (v === false) return "Não";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

export default function Audit() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    const params = { page, limit: 20 };
    if (search) params.search = search;
    api.get("/audit", { params }).then(({ data }) => setData(data));
  }, [page, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="audit-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ScrollText size={26} className="text-primary" /> Governança e Auditoria
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{data.total} registro(s) — trilha completa de ações no sistema (usuário, ação, IP, dispositivo).</p>
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por usuário, ação ou detalhe..." className="pl-9" data-testid="audit-search-input" />
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-muted/40">
                <th className="px-4 py-3 font-medium">Data / Hora</th>
                <th className="px-4 py-3 font-medium">Usuário</th>
                <th className="px-4 py-3 font-medium">Ação</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">Detalhe</th>
                <th className="px-4 py-3 font-medium hidden lg:table-cell">IP</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Nenhum registro de auditoria.</td></tr>
              )}
              {data.items.map((log) => (
                <Fragment key={log.id}>
                  <tr className={`border-b border-border/50 last:border-0 ${log.changes?.length ? "cursor-pointer hover:bg-muted/30" : ""}`}
                    onClick={() => log.changes?.length && setExpanded(expanded === log.id ? null : log.id)} data-testid="audit-row">
                    <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{formatDate(log.created_at)}</td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-xs">{log.user_name}</p>
                      <p className="text-[10px] text-muted-foreground">{ROLE_LABELS[log.role] || log.role}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1.5">
                        <Badge variant="outline" className="font-mono text-[10px]">{log.action}</Badge>
                        {log.changes?.length > 0 && (
                          <ChevronDown size={12} className={`text-muted-foreground transition-transform ${expanded === log.id ? "rotate-180" : ""}`} />
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell text-xs text-muted-foreground max-w-md truncate">{log.detail}</td>
                    <td className="px-4 py-2.5 hidden lg:table-cell text-xs text-muted-foreground font-mono">{log.ip}</td>
                  </tr>
                  {expanded === log.id && log.changes?.length > 0 && (
                    <tr className="border-b border-border/50 bg-muted/20" data-testid="audit-changes-row">
                      <td colSpan={5} className="px-4 py-3">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Alterações registradas (valor anterior → novo valor)</p>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                          {log.changes.map((ch, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs rounded-md border border-border bg-card px-2.5 py-1.5">
                              <span className="font-medium shrink-0">{ch.field}:</span>
                              <span className="text-red-400/80 line-through truncate max-w-[140px]">{fmtVal(ch.old)}</span>
                              <ArrowRight size={11} className="text-muted-foreground shrink-0" />
                              <span className="text-emerald-400 truncate">{fmtVal(ch.new)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-xs text-muted-foreground">Página {data.page || page} de {data.pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-btn"><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="audit-next-btn"><ChevronRight size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
